import { PURPOSES, STYLE_WORDS } from '../../pages/EmployeePortal/Warmups/warmupShape';

/**
 * The warmup player's shared vocabulary and formatting — the ONE place these
 * mappings live, so the console, the routine list and the settings block can
 * be rendered in different parts of the tree and still agree.
 *
 * Split out of WarmupPlayer.js on 2026-09-04 (Lesson Dashboard I1, phase 0)
 * when the player's deck and its routine list stopped being siblings. Nothing
 * here changed in that move; it is the same code at a new address.
 */

export const PURPOSE_LABEL = Object.fromEntries(PURPOSES.map(([key, label]) => [key.replace(/-/g, ''), label]));
export const LEVEL_LABEL = { beg: 'Beginner', int: 'Intermediate', adv: 'Advanced' };
/**
 * What the guide line sings on: [engine value, label]. Labelled "Sung", not
 * "Voice" — the setup panel's Voice field is the STUDENT's range (low / mid /
 * high), and two controls called Voice on one screen is a wrong tap waiting.
 */
export const VOICE_OPTIONS = [
	['auto', 'Auto'],
	['voice', 'Sung'],
	['piano', 'Piano'],
];

/** The set's own name for the sustained option ("Clarinet"), else the generic label. */
export const sustainedLabel = (state) => state.voiceLabel || VOICE_OPTIONS.find(([v]) => v === 'voice')[1];

/** What the button for one option reads: Auto shows what it chose for this exercise. */
export const voiceOptionLabel = (state, value, label) => {
	if (value === 'voice') return sustainedLabel(state);
	if (value === 'auto' && state.voice === 'auto' && state.voiceEffective) return `Auto · ${state.voiceEffective === 'voice' ? sustainedLabel(state) : 'Piano'}`;
	return label;
};

/** Time signature choices for the player: [beats per bar, label]. */
export const METER_OPTIONS = [
	[4, '4/4'],
	[3, '3/4'],
	[2, '2/4'],
];
/** The accompaniment figure under the guide: the generator's chords, or Andy's walking bass and cues (2026-09-08). */
export const ACCOMPANIMENT_OPTIONS = [['simple', 'Simple'], ['full', 'Full']];

export const GUIDE_LEVELS = [
	['full', 'Guide on'],
	['low', 'Guide low'],
	['off', 'Guide off'],
];

export const DEFAULT_CELL = { duration: 'mid', level: 'beg', ageBand: '11-13', style: 'legit', voice: 'mid' };

export const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

export const phaseLabel = (state) => {
	if (state.countingIn) return 'Count-in';
	if (state.status === 'paused') return 'Paused';
	if (state.status === 'done') return 'Finished';
	if (state.status !== 'playing') return '';
	if (state.phase === 'hold') return 'Holding at the top · Next goes one higher';
	if (state.phase === 'down') return 'Coming down';
	// The walk climbs past the plan until the teacher taps Descend (2026-10-02),
	// so say so once it has, or the room reads a key above "planned top" as a fault.
	if (state.tonic != null && state.top != null && state.tonic > state.top) return 'Past the planned top · Descend brings it down';
	return 'Climbing';
};

/** The disclosure summary line: "Mid · Beginner · 11-13 · legit". */
export const cellSummary = (cell, durationWords) =>
	`${(durationWords[cell.duration] || cell.duration).split(' · ')[0]} · ${LEVEL_LABEL[cell.level] || cell.level} · ${cell.ageBand} · ${STYLE_WORDS[cell.style] || cell.style}`;
